/**
 * Giao nộp chứng cứ – Đương sự bổ sung chứng cứ cho vụ án đã được thụ lý.
 * Bước riêng sau "Nộp đơn khởi kiện", phản ánh quy trình tố tụng thực tế.
 * Mock: lịch sử giao nộp, gợi ý chứng cứ theo loại vụ, danh sách file đính kèm.
 */
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
  Alert,
  Snackbar,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
} from '@mui/material'
import { UploadFile, ArrowBack, Send, Description, InfoOutlined, History, DeleteOutline, InsertDriveFile } from '@mui/icons-material'
import { PageDataState } from '../../components/PageDataState'
import { useAppSelector } from '../../hooks/redux'
import { useGetCasesQuery } from '../../store/api/casesApi'
import type { CaseStatus } from '../../types/case'
import type { CaseCategory } from '../../types/case'

const STATUS_LABELS: Record<string, string> = {
  filed: 'Đã nộp',
  accepted: 'Đã thụ lý',
  pre_trial: 'Chuẩn bị xét xử',
  trial: 'Đang xét xử',
}

const CATEGORY_LABELS: Record<CaseCategory, string> = {
  marriage_family: 'Hôn nhân và gia đình',
  civil: 'Dân sự',
  land: 'Đất đai',
}

/** Gợi ý chứng cứ thường cần theo loại vụ (mock). */
const EVIDENCE_SUGGESTIONS: Record<CaseCategory, string[]> = {
  marriage_family: ['Giấy chứng nhận kết hôn', 'CMND/CCCD các bên', 'Đơn xin ly hôn (bản scan)', 'Giấy khai sinh con chung', 'Biên lai tài sản chung (nếu tranh chấp tài sản)', 'Xác nhận thu nhập'],
  civil: ['Hợp đồng, giao dịch liên quan', 'Biên lai thanh toán', 'Email, tin nhắn trao đổi', 'Công văn, thông báo', 'Báo cáo định giá (nếu có)'],
  land: ['Giấy chứng nhận quyền sử dụng đất', 'Sổ đỏ, trích lục', 'Hợp đồng chuyển nhượng/thuê', 'Biên bản đo đạc', 'Ảnh hiện trạng thửa đất'],
}

/** Mock lịch sử giao nộp chứng cứ theo case_id. */
const MOCK_EVIDENCE_HISTORY: Record<string, Array<{ id: string; submitted_at: string; description: string; files: string[]; status: string }>> = {
  'case-1': [
    { id: 'ev1-1', submitted_at: '2026-02-05T14:00:00Z', description: 'Bổ sung Giấy chứng nhận kết hôn, CMND hai bên theo yêu cầu của Tòa.', files: ['GCN_ket_hon.pdf', 'CMND_nguyen_don.pdf', 'CMND_bi_don.pdf'], status: 'Đã tiếp nhận' },
    { id: 'ev1-2', submitted_at: '2026-02-20T09:30:00Z', description: 'Bổ sung biên lai mua sắm tài sản chung trong thời kỳ hôn nhân.', files: ['Bien_lai_tai_san.pdf'], status: 'Đã tiếp nhận' },
  ],
  'case-3': [
    { id: 'ev3-1', submitted_at: '2026-02-18T10:00:00Z', description: 'Chứng cứ kèm theo đơn: Giấy CN kết hôn, đơn ly hôn.', files: ['GCN_ket_hon.pdf', 'Don_ly_hon_scan.pdf'], status: 'Đã tiếp nhận' },
  ],
  'case-4': [
    { id: 'ev4-1', submitted_at: '2026-01-15T11:00:00Z', description: 'Hợp đồng vay tiền và biên lai chuyển khoản.', files: ['Hop_dong_vay.pdf', 'Bien_lai_chuyen_khoan.pdf'], status: 'Đã tiếp nhận' },
  ],
  'case-5': [],
  'case-2': [],
  'case-6': [],
  'case-7': [],
  'case-demo': [],
}

/** Các trạng thái vụ án cho phép giao nộp chứng cứ (đã qua bước nộp đơn). */
const EVIDENCE_ALLOWED_STATUSES: CaseStatus[] = ['filed', 'accepted', 'pre_trial', 'trial']

export default function EvidenceSubmitPage() {
  const navigate = useNavigate()
  const user = useAppSelector((s) => s.auth.user)
  const { data: rawCases = [], isLoading, error, refetch } = useGetCasesQuery()
  const [selectedCaseId, setSelectedCaseId] = useState<string>('')
  const [description, setDescription] = useState('')
  const [attachedFiles, setAttachedFiles] = useState<{ name: string; size: string }[]>([])
  const [snack, setSnack] = useState('')

  const cases = (user?.role === 'user' || user?.role === 'litigant' || user?.role === 'student')
    ? rawCases.filter((c) => c.plaintiff_id === user?.id && EVIDENCE_ALLOWED_STATUSES.includes(c.status as CaseStatus))
    : rawCases.filter((c) => EVIDENCE_ALLOWED_STATUSES.includes(c.status as CaseStatus))

  const selectedCase = cases.find((c) => c.id === selectedCaseId)
  const evidenceHistory = selectedCaseId ? (MOCK_EVIDENCE_HISTORY[selectedCaseId] ?? []) : []
  const suggestions = selectedCase?.category ? EVIDENCE_SUGGESTIONS[selectedCase.category] ?? [] : []

  const handleAddFile = () => {
    const names = ['Tai_lieu_bo_sung_1.pdf', 'Anh_chung_cu_01.jpg', 'Bien_ban_xac_nhan.pdf']
    const sizes = ['1.2 MB', '2.5 MB', '856 KB']
    const i = attachedFiles.length % 3
    setAttachedFiles((prev) => [...prev, { name: names[i], size: sizes[i] }])
  }

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = () => {
    if (!selectedCaseId) return
    setSnack('Đã gửi bổ sung chứng cứ. Tòa án sẽ xem xét và thông báo kết quả qua Cổng dịch vụ công.')
    setDescription('')
    setAttachedFiles([])
    setSelectedCaseId('')
  }

  const formatClaimValue = (vnd: number) => {
    if (vnd >= 1e9) return `${(vnd / 1e9).toFixed(1)} tỷ VNĐ`
    return `${(vnd / 1e6).toFixed(0)} triệu VNĐ`
  }

  return (
    <Box sx={{ width: '100%', maxWidth: 800, mx: 'auto', pb: 4 }}>
      <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mb: 2 }}>
        Quay lại
      </Button>

      <Typography variant="h5" fontWeight={600} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        <UploadFile /> Giao nộp chứng cứ
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Sau khi đơn khởi kiện đã được Tòa án tiếp nhận, bạn có thể bổ sung chứng cứ cho vụ án tại đây. Chọn vụ án, mô tả nội dung và đính kèm tài liệu (PDF, ảnh). Tòa sẽ thông báo kết quả tiếp nhận qua hệ thống.
      </Typography>

      <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover', borderRadius: 2 }}>
        <Typography variant="subtitle2" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
          <InfoOutlined fontSize="small" /> Hướng dẫn giao nộp
        </Typography>
        <Typography variant="body2" color="text.secondary" component="ul" sx={{ m: 0, pl: 2 }}>
          <li>Chứng cứ giao nộp bổ sung phải liên quan trực tiếp đến vụ án; nên ghi rõ nội dung từng tài liệu trong phần mô tả.</li>
          <li>Định dạng: PDF, JPG, PNG. Tối đa 10 MB/file; tổng dung lượng mỗi lần gửi không quá 50 MB.</li>
          <li>Thời hạn giao nộp chứng cứ do Tòa án ấn định (thông báo thụ lý, giấy triệu tập). Giao nộp quá hạn có thể không được xem xét.</li>
        </Typography>
      </Paper>

      <PageDataState isLoading={isLoading} error={error} empty={cases.length === 0} emptyMessage="Chưa có vụ án nào đang trong giai đoạn nhận chứng cứ. Vui lòng nộp đơn khởi kiện trước." onRetry={() => refetch()}>
        {cases.length > 0 && (
          <Card sx={{ mb: 3, borderRadius: 2, boxShadow: 1 }}>
            <CardContent>
              <FormControl fullWidth sx={{ mb: 3 }}>
                <InputLabel>Chọn vụ án cần giao nộp chứng cứ</InputLabel>
                <Select
                  value={selectedCaseId}
                  label="Chọn vụ án cần giao nộp chứng cứ"
                  onChange={(e) => { setSelectedCaseId(e.target.value); setAttachedFiles([]); setDescription('') }}
                >
                  {cases.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.case_code} – {CATEGORY_LABELS[c.category]} – {STATUS_LABELS[c.status] ?? c.status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {selectedCase && (
                <>
                  <Box sx={{ mb: 3, p: 2, borderRadius: 2, border: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>Thông tin vụ án</Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
                      <Chip label={selectedCase.case_code} color="primary" size="small" />
                      <Chip label={CATEGORY_LABELS[selectedCase.category]} variant="outlined" size="small" />
                      <Chip label={STATUS_LABELS[selectedCase.status] ?? selectedCase.status} variant="outlined" size="small" />
                    </Box>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      <strong>Nguyên đơn:</strong> {selectedCase.plaintiff_name ?? selectedCase.plaintiff_id} &nbsp;·&nbsp; <strong>Bị đơn:</strong> {selectedCase.defendant_name ?? selectedCase.defendant_id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Giá trị tranh chấp: {formatClaimValue(selectedCase.claim_value_vnd)} · Thẩm phán: {selectedCase.assigned_judge_name ?? 'Chưa phân công'}
                    </Typography>
                    {selectedCase.deadline_at && (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                        Hạn nộp chứng cứ (theo thông báo): {new Date(selectedCase.deadline_at).toLocaleDateString('vi-VN')}
                      </Typography>
                    )}
                  </Box>

                  {suggestions.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>Gợi ý chứng cứ thường cần ({CATEGORY_LABELS[selectedCase.category]})</Typography>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                        {suggestions.map((s) => (
                          <Chip key={s} label={s} size="small" variant="outlined" sx={{ fontSize: 11 }} />
                        ))}
                      </Box>
                    </Box>
                  )}

                  {evidenceHistory.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                        <History fontSize="small" /> Lịch sử giao nộp chứng cứ
                      </Typography>
                      <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                        <List dense disablePadding>
                          {evidenceHistory.map((h, idx) => (
                            <ListItem key={h.id} divider={idx < evidenceHistory.length - 1}>
                              <ListItemText
                                primary={h.description}
                                secondary={
                                  <>
                                    {new Date(h.submitted_at).toLocaleString('vi-VN')} · {h.files.join(', ')} · <Chip label={h.status} size="small" color="success" sx={{ ml: 0.5, height: 18 }} />
                                  </>
                                }
                                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                                secondaryTypographyProps={{ variant: 'caption', component: 'span' }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Paper>
                    </Box>
                  )}

                  <Divider sx={{ my: 2 }} />
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>Bổ sung chứng cứ lần này</Typography>

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Nội dung/mô tả chứng cứ giao nộp"
                    placeholder="Ví dụ: Bổ sung Giấy chứng nhận quyền sử dụng đất số ... do UBND quận cấp ngày ...; Ảnh hiện trạng thửa đất..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    helperText="Ghi rõ từng loại tài liệu và nội dung liên quan đến yêu cầu của bạn."
                    sx={{ mb: 2 }}
                  />

                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Description fontSize="small" /> Đính kèm tài liệu
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 2 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        PDF, JPG, PNG. Tối đa 10 MB/file. Kéo thả file vào đây hoặc bấm chọn file.
                      </Typography>
                      <Button variant="outlined" size="small" startIcon={<UploadFile />} onClick={handleAddFile}>
                        Chọn file (mock thêm)
                      </Button>
                      {attachedFiles.length > 0 && (
                        <List dense sx={{ mt: 1 }}>
                          {attachedFiles.map((f, i) => (
                            <ListItem key={i} sx={{ py: 0.25, borderBottom: 1, borderColor: 'divider' }}>
                              <InsertDriveFile fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                              <ListItemText primary={f.name} secondary={f.size} primaryTypographyProps={{ variant: 'body2' }} secondaryTypographyProps={{ variant: 'caption' }} />
                              <ListItemSecondaryAction>
                                <IconButton size="small" onClick={() => handleRemoveFile(i)} aria-label="Xóa file"><DeleteOutline fontSize="small" /></IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </Paper>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Button variant="contained" startIcon={<Send />} onClick={handleSubmit} disabled={!selectedCaseId}>
                      Gửi chứng cứ
                    </Button>
                    <Button variant="outlined" onClick={() => navigate(`/cases/${selectedCase.id}`)}>
                      Xem chi tiết vụ án
                    </Button>
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        )}
      </PageDataState>

      <Snackbar open={!!snack} autoHideDuration={5000} onClose={() => setSnack('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  )
}
